import { useState, useCallback, useEffect } from 'react';
import { Settings } from 'lucide-react';
import { useIsMobile } from './use-mobile';
import { supabase } from '../integrations/supabase/client';
import { ensureDir, ensureFile } from '@/OS/utils/filesystem';
import { useToast } from '../components/ui/use-toast';
import React from 'react';
import { PROGRAM_MAP } from '../utils/programs';
import { makeProgramIcon } from '../components/AppIcon';
import {
  applyInterruptedStartupRecovery,
  clearOsStartupInProgress,
  clearPersistedAiChatTabs,
  clearPersistedWindowStateForApp,
  markOsStartupInProgress,
  removeAppWindowsFromSnapshot,
  wasPreviousOsStartupInterrupted,
} from '@/OS/lib/appRecovery';

export interface WindowData {
  id: string;
  appId: string;
  title: string;
  isMinimized: boolean;
  isMaximized: boolean;
  zIndex: number;
  x: number;
  y: number;
  width: number;
  height: number;
  originalX?: number;
  originalY?: number;
  originalWidth?: number;
  originalHeight?: number;
  filePath?: string;
  fileContent?: string;
  fixedSize?: boolean; // Prevents resizing and maximizing
  canMaximize?: boolean; // Controls if maximize button is enabled
  route?: string; // NEW: route metadata for duplicate window detection
  hiddenInTray?: boolean;
}

export interface DesktopIcon {
  id: string;
  appId: string;
  label: string;
  icon: React.ComponentType<any>;
  x: number;
  y: number;
  filePath?: string; // For files/folders created on desktop
  isEditing?: boolean; // For rename functionality
}

export const useOS = () => {
  const isMobile = useIsMobile();
  const [openWindows, setOpenWindows] = useState<WindowData[]>([]);
  const [isStartMenuOpen, setIsStartMenuOpen] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [nextZIndex, setNextZIndex] = useState(100);

  const sb: any = supabase;
  const interruptedStartupRef = React.useRef(wasPreviousOsStartupInterrupted());

  // FIX: initialize toast for use in callbacks and effects
  const { toast } = useToast();

  // Track authenticated user and remote row
  const [userId, setUserId] = useState<string | null>(null);
  const [desktopStateRowId, setDesktopStateRowId] = useState<string | null>(null);

  // PERSISTENCE KEY
  const PERSIST_KEY = 'mapleos_state_v1';

  const recoverInterruptedSnapshot = useCallback((windows: WindowData[]) => {
    return interruptedStartupRef.current ? applyInterruptedStartupRecovery(windows) : windows;
  }, []);

  // Helper: ensure windows fit the current viewport
  const sanitizeWindows = (windows: WindowData[]) => {
    const isElectron = typeof globalThis.window !== 'undefined' && (globalThis.window as any).electronAPI?.isElectron;
    const TITLE_BAR_HEIGHT = isElectron ? 32 : 0; // 32px title bar in Electron
    const TASKBAR_HEIGHT = 48; // 48px taskbar at bottom

    const viewportWidth = globalThis.window.innerWidth;
    const viewportHeight = globalThis.window.innerHeight - TASKBAR_HEIGHT - TITLE_BAR_HEIGHT;
    const MIN_WIDTH = 200;
    const MIN_HEIGHT = 150;

    return windows.map((w) => {
      if (w.isMaximized) {
        return {
          ...w,
          x: 0,
          y: TITLE_BAR_HEIGHT,
          width: viewportWidth,
          height: viewportHeight,
        };
      }

      const width = Math.max(MIN_WIDTH, Math.min(w.width, viewportWidth));
      const height = Math.max(MIN_HEIGHT, Math.min(w.height, viewportHeight));
      const x = Math.max(0, Math.min(w.x, viewportWidth - width));
      const y = Math.max(TITLE_BAR_HEIGHT, Math.min(w.y, viewportHeight - height + TITLE_BAR_HEIGHT));

      return { ...w, width, height, x, y };
    });
  };

  // Helper: resolve Desktop root (consistent with Desktop sync logic)
  const getDesktopRoot = useCallback(() => {
    // Keep consistent with current Desktop implementation ("Vince")
    return 'C:\\User\\Vince\\Desktop';
  }, []);

  type TemplateFile = { path: string; content?: string };
  type TemplateWindow = { appId: string; title: string; filePath?: string; fileContent?: string; route?: string };
  type TemplateSpec = {
    folders: string[];
    files: TemplateFile[];
    windows: TemplateWindow[];
  };

  const formatDate = (d = new Date()) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const applyVars = (text: string, vars: Record<string, string>) => {
    return Object.keys(vars).reduce(
      (acc, key) => acc.split(`{${key}}`).join(vars[key]),
      text
    );
  };

  const getTemplateSpec = (name: string, vars: Record<string, string>): TemplateSpec | null => {
    const desktop = getDesktopRoot();
    const dateStr = vars.Date || formatDate();

    if (name === 'client-workspace') {
      const client = vars.ClientName || 'Client';
      const base = `${desktop}\\${client}`;
      const proposalContent = `# Proposal for {ClientName}

This is a draft proposal automatically generated.
- Overview
- Goals
- Timeline
- Budget
`;
      const slideOutline = `Title: {ClientName} Project Overview
Slide 1: Objectives
Slide 2: Scope
Slide 3: Timeline
Slide 4: Budget
Slide 5: Risks
Slide 6: Next Steps
`;
      return {
        folders: [
          base,
          `${base}\\Docs`,
          `${base}\\Slides`,
          `${base}\\Assets`,
        ],
        files: [
          { path: `${base}\\Docs\\Proposal.md`, content: applyVars(proposalContent, vars) },
          { path: `${base}\\Slides\\Outline.mapleslide`, content: applyVars(slideOutline, vars) },
        ],
        windows: [
          { appId: 'maple-slide', title: `MapleSlide - ${client} Outline`, filePath: `${base}\\Slides\\Outline.mapleslide`, fileContent: applyVars(slideOutline, vars) },
          { appId: 'notepad', title: `Notepad - Proposal.md`, filePath: `${base}\\Docs\\Proposal.md`, fileContent: applyVars(proposalContent, vars) },
          { appId: 'file-explorer', title: `${client} Assets`, filePath: `${base}\\Assets` },
        ]
      };
    }

    if (name === 'data-briefing') {
      const briefBase = `${desktop}\\Briefings\\${dateStr}`;
      const findings = vars.FindingsContent || `# Data Briefing - ${dateStr}

Summary:
- Rows: {RowCount}
- Columns: {ColumnCount}
- Columns: {Columns}

Key Findings:
- Add notable trends, outliers, and correlations here.
`;

      const deck = vars.DeckContent || `Title: Briefing ${dateStr}
Slide 1: Executive Summary
Slide 2: Dataset Overview
Slide 3: Key Trends
Slide 4: Outliers & Anomalies
Slide 5: Recommendations
Slide 6: Next Steps
`;

      return {
        folders: [
          `${desktop}\\Briefings`,
          briefBase,
          `${briefBase}\\Assets`,
        ],
        files: [
          { path: `${briefBase}\\Findings.md`, content: applyVars(findings, vars) },
          { path: `${briefBase}\\Deck.mapleslide`, content: applyVars(deck, vars) },
          // Optional: Save CSV if provided
          ...(vars.CsvText ? [{ path: `${briefBase}\\Assets\\dataset.csv`, content: vars.CsvText }] : [])
        ],
        windows: [
          { appId: 'maple-slide', title: `MapleSlide - Briefing ${dateStr}`, filePath: `${briefBase}\\Deck.mapleslide`, fileContent: applyVars(deck, vars) },
          { appId: 'notepad', title: `Notepad - Findings.md`, filePath: `${briefBase}\\Findings.md`, fileContent: applyVars(findings, vars) },
          ...(vars.CsvText ? [{ appId: 'file-explorer', title: `Briefing Assets`, filePath: `${briefBase}\\Assets` }] : [])
        ]
      };
    }

    if (name === 'bug-repro') {
      const base = `${desktop}\\Repro`;
      const steps = `Bug Reproduction Steps

1. Open the application
2. Navigate to the affected feature
3. Perform the action that triggers the bug
4. Observe the error behavior

Expected:
Actual:
`;
      const observations = `Observations

- Environment:
- Browser/OS:
- Frequency:
- Logs:
`;
      const placeholder = 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1200&h=800&fit=crop';
      return {
        folders: [
          base,
          `${base}\\Artifacts`,
        ],
        files: [
          { path: `${base}\\Steps.txt`, content: steps },
          { path: `${base}\\Observations.txt`, content: observations },
          { path: `${base}\\Artifacts\\screenshot.jpg`, content: placeholder },
        ],
        windows: [
          { appId: 'image-viewer', title: 'Image Viewer - screenshot.jpg', filePath: `${base}\\Artifacts\\screenshot.jpg`, fileContent: placeholder },
          { appId: 'bug-report', title: 'Bug Report' },
          { appId: 'file-explorer', title: 'Repro Folder', filePath: base },
        ]
      };
    }

    return null;
  };

  const focusWindow = useCallback((windowId: string) => {
    setOpenWindows(prev => 
      prev.map(w => 
        w.id === windowId 
          ? { ...w, isMinimized: false, zIndex: nextZIndex, hiddenInTray: false }
          : w
      )
    );
    setNextZIndex(prev => prev + 1);
  }, [nextZIndex]);

  // NEW: Track in-flight opens to prevent duplicates during racey mounts
  const inFlightOpensRef = React.useRef<Map<string, number>>(new Map());
  const markOpenInFlight = (key: string) => {
    const now = Date.now();
    inFlightOpensRef.current.set(key, now);
    setTimeout(() => {
      if (inFlightOpensRef.current.get(key) === now) {
        inFlightOpensRef.current.delete(key);
      }
    }, 3000);
  };
  const isOpenInFlight = (key: string) => {
    const t = inFlightOpensRef.current.get(key);
    return typeof t === 'number' && (Date.now() - t) < 3000;
  };

  const openWindow = useCallback((appId: string, title: string, filePath?: string, fileContent?: string, options?: { route?: string; focus?: boolean }) => {
    // Single-instance rules for apps that must stay in one MapleOS window
    if (appId === 'maple-access' || appId === 'web-browser') {
      const existingWindow = openWindows.find(w => w.appId === appId);
      if (existingWindow) {
        focusWindow(existingWindow.id);
        if (appId === 'maple-access' && typeof filePath === 'string' && filePath.length > 0) {
          window.dispatchEvent(new CustomEvent('maple-access:open-target', { detail: { filePath } }));
        }
        return;
      }

      const key = `single:${appId}`;
      if (isOpenInFlight(key)) {
        return;
      }
      markOpenInFlight(key);
    }

    // Prefer filePath for deduplication for all apps
    const existing = openWindows.find(w => {
      if (filePath) {
        return w.appId === appId && w.filePath === filePath;
      }
      if (options?.route) {
        return w.appId === appId && w.route === options.route;
      }
      return w.appId === appId && w.title === title;
    });

    if (existing) {
      focusWindow(existing.id);
      return;
    }

    // In-flight guard: prefer filePath in key; fallback to route/title
    const key = `${appId}:${filePath || ''}:${options?.route || ''}`;
    if (isOpenInFlight(key)) {
      return;
    }
    markOpenInFlight(key);
    
    const windowId = `${appId}-${Date.now()}`;
    
    // Set larger default size for image viewer
    let width = 800;
    let height = 600;
    let fixedSize = false;
    let canMaximize = true;
    
    if (appId === 'image-viewer') {
      width = 1000;
      height = 700;
    } else if (appId === 'web-browser') {
      width = 1280;
      height = 820;
    } else if (appId === 'disk-defrag') {

      // Disk Defragmenter has a fixed size to prevent scrollbars
      width = 970;
      height = 700;
      fixedSize = true;
      canMaximize = false;
    } else if (appId === 'office-suite' || appId === 'maple-word' || appId === 'maple-sheet') {
      // Office applications need larger windows
      width = 1200;
      height = 800;
    } else if (appId === 'maple-slide') {
      // Presentation app needs extra wide window
      width = 1400;
      height = 900;
    } else if (appId === 'maple-calc') {
      // Calculator needs a compact window
      width = 530;
      height = 730;
    } else if (appId === 'maple-money') {
      // Personal finance app needs a large window
      width = 900;
      height = 700;
    } else if (appId === 'hex-editor') {
      // Hex editor needs a large window for viewing data
      width = 1000;
      height = 700;
    } else if (appId === 'code-editor') {
      // Code editor needs a large window for coding
      width = 1200;
      height = 800;
    } else if (appId === 'profile') {
      // Profile needs more vertical room for account settings and password controls
      width = 960;
      height = 760;
    } else if (appId === 'task-manager') {
      // Task Manager should open wide enough for dashboards and process lists
      width = 1180;
      height = 820;
    } else if (appId === 'ai-chat') {
      // AI Chat should open with extra room for the chat grid
      width = 1200;
      height = 800;
    } else if (appId === 'ai-knowledge') {
      // AI Knowledge needs a wide split-pane workspace for knowledge bases and documents
      width = 1240;
      height = 820;
    } else if (appId === 'canxp-module') {

      // Generic module window for embedded CanXP pages
      width = 1200;
      height = 800;
    } else if (appId === 'maple-gis') {
      // MapleGIS globe app
      width = 1400;
      height = 900;
    } else if (appId === 'pacman-runner') {
      // Pac-Man easter egg fits best in a wide arcade-style panel
      width = 1100;
      height = 520;
    }

    // Auto-maximize on mobile if window supports it
    const shouldMaximizeOnMobile = isMobile && canMaximize && !fixedSize;

    const newWindow: WindowData = {
      id: windowId,
      appId,
      title,
      isMinimized: false,
      isMaximized: shouldMaximizeOnMobile,
      zIndex: nextZIndex,
      x: shouldMaximizeOnMobile ? 0 : 100 + Math.random() * 200,
      y: shouldMaximizeOnMobile ? 0 : 52 + Math.random() * 100,
      width: shouldMaximizeOnMobile ? globalThis.window.innerWidth : width,
      height: shouldMaximizeOnMobile ? globalThis.window.innerHeight - 48 : height,
      originalX: shouldMaximizeOnMobile ? 100 + Math.random() * 200 : undefined,
      originalY: shouldMaximizeOnMobile ? 52 + Math.random() * 100 : undefined,

      originalWidth: shouldMaximizeOnMobile ? width : undefined,
      originalHeight: shouldMaximizeOnMobile ? height : undefined,
      filePath,
      fileContent,
      fixedSize,
      canMaximize,
      route: options?.route, // NEW
      hiddenInTray: false,
    };

    setOpenWindows(prev => [...prev, newWindow]);
    setNextZIndex(prev => prev + 1);

    //toast({ title: 'Opened window', description: title });
    if (userId) {
      // Anonymous-ish event (user_id captured via RLS), safe payload
      sb.from('usage_analytics').insert({
        user_id: userId,
        event_type: 'os_tool_action',
        event_data: { action: 'openWindow', appId, title, route: options?.route, filePath: !!filePath },
      });
    }
  }, [nextZIndex, isMobile, openWindows, focusWindow, toast, userId]);

  const applyTemplate = useCallback(async (name: string, vars: Record<string, string> = {}) => {
    const spec = getTemplateSpec(name, vars);
    if (!spec) return false;

    // Create folders idempotently
    for (const folder of spec.folders) {
      await ensureDir(folder);
    }

    // Create files idempotently
    for (const file of spec.files) {
      await ensureFile(file.path, file.content ?? '');
    }

    // Open windows (no duplicates per useOS.openWindow logic)
    for (const w of spec.windows) {
      openWindow(w.appId, w.title, w.filePath, w.fileContent, { route: w.route });
    }

    // Notify desktop to re-sync
    const desktopPath = getDesktopRoot();
    window.dispatchEvent(new CustomEvent('fileSystemChanged', { detail: { path: desktopPath } }));

    toast({ title: 'Template applied', description: `${name} created files and windows.` });
    if (userId) {
      sb.from('usage_analytics').insert({
        user_id: userId,
        event_type: 'os_tool_action',
        event_data: { action: 'applyTemplate', template: name, vars },
      });
    }
    return true;
  }, [openWindow, getDesktopRoot, toast, userId]);

  // Restore persisted windows on load (local first)
  useEffect(() => {
    markOsStartupInProgress();

    if (interruptedStartupRef.current) {
      try {
        const raw = localStorage.getItem(PERSIST_KEY);
        if (raw) {
          const parsed = JSON.parse(raw) as { windows?: WindowData[]; nextZIndex?: number };
          const recoveredWindows = removeAppWindowsFromSnapshot(parsed.windows || [], 'ai-chat');
          localStorage.setItem(
            PERSIST_KEY,
            JSON.stringify({
              ...parsed,
              windows: recoveredWindows,
            })
          );
        }
      } catch {
        localStorage.removeItem(PERSIST_KEY);
      }

      clearPersistedAiChatTabs();
    }

    const timer = globalThis.window.setTimeout(() => {
      clearOsStartupInProgress();
      interruptedStartupRef.current = false;
    }, 8000);

    return () => {
      globalThis.window.clearTimeout(timer);
    };
  }, [PERSIST_KEY]);

  // Restore persisted windows on load (local first)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(PERSIST_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { windows?: WindowData[]; nextZIndex?: number };
        const windows = sanitizeWindows(recoverInterruptedSnapshot(parsed.windows || []));
        // Only restore if no windows are open yet (avoid overwriting freshly opened ones)
        if (openWindows.length === 0 && windows.length > 0) {
          setOpenWindows(windows);

          // Ensure nextZIndex is ahead of any restored windows
          const highestZ = windows.reduce((max, w) => Math.max(max, w.zIndex || 0), 100);
          const restoredZ = Math.max(parsed.nextZIndex || 100, highestZ + 1);
          setNextZIndex(restoredZ);
        } else {
          console.log('[useOS] Skipping local restore: windows already open or no saved windows.');
        }
      }
    } catch {
      // Ignore parse errors to avoid breaking the OS
    }
  }, []);

  // Persist windows locally whenever they change
  useEffect(() => {
    // Build a lightweight snapshot that omits heavy fields like fileContent
    const lightweightWindows = openWindows.map((w) => {
      const {
        id, appId, title, isMinimized, isMaximized, zIndex,
        x, y, width, height,
        originalX, originalY, originalWidth, originalHeight,
        filePath, // keep
        // DO NOT persist fileContent to avoid quota issues
        fixedSize, canMaximize,
        route,
        hiddenInTray,
      } = w;

      return {
        id, appId, title, isMinimized, isMaximized, zIndex,
        x, y, width, height,
        originalX, originalY, originalWidth, originalHeight,
        filePath,
        fixedSize, canMaximize,
        route,
        hiddenInTray,
      };
    });

    const payload = { windows: lightweightWindows, nextZIndex };

    try {
      localStorage.setItem(PERSIST_KEY, JSON.stringify(payload));
    } catch (err) {
      // If quota is exceeded or serialization fails, fall back to storing a minimal state
      try {
        const minimal = {
          windows: lightweightWindows.slice(-5), // keep last few windows only
          nextZIndex
        };
        localStorage.setItem(PERSIST_KEY, JSON.stringify(minimal));
      } catch {
        // As a last resort, clear the persisted state so the OS won't crash
        localStorage.removeItem(PERSIST_KEY);
      }
    }
  }, [openWindows, nextZIndex]);

  // Detect auth user and keep userId in sync
  useEffect(() => {
    const init = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      setUserId(sessionData.session?.user?.id ?? null);
    };
    init();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  // NEW: Update "My Documents" icon path based on current user's profile/username
  useEffect(() => {
    const updateDocumentsIconPath = async () => {
      // Resolve username: prefer profiles.username, else email handle, else "User"
      const { data: sessionData } = await supabase.auth.getSession();
      const user = sessionData.session?.user;
      let username = 'User';

      if (user) {
        const { data } = await supabase
          .from('profiles')
          .select('username,email')
          .eq('id', user.id)
          .maybeSingle();

        const profile = (data ?? null) as { username?: string | null; email?: string | null } | null;
        const candidate =
          profile?.username ||
          (profile?.email || user.email || 'User').split('@')[0] ||
          'User';

        username = (candidate.replace(/[^a-zA-Z0-9]/g, '') || 'User');
      }

      const documentsPath = `C:\\User\\${username}\\Documents`;

      setDesktopIcons(prev =>
        prev.map(icon =>
          icon.id === 'my-documents' ? { ...icon, filePath: documentsPath } : icon
        )
      );
    };

    updateDocumentsIconPath();
  }, [userId]);

  // Load remote desktop state when logged in (prefer cloud copy)
  useEffect(() => {
    if (!userId) return;

    const loadRemote = async () => {
      const { data } = await sb
        .from('os_user_desktop')
        .select('id, config_value, updated_at')
        .eq('user_id', userId)
        .eq('config_key', 'windows_state_v1')
        .order('updated_at', { ascending: false })
        .limit(1);

      const row = (data && data[0]) || null;
      if (row?.config_value) {
        const remote = row.config_value as { windows?: WindowData[]; nextZIndex?: number };
        const windows = sanitizeWindows(recoverInterruptedSnapshot(remote.windows || []));

        if (openWindows.length === 0 && windows.length > 0) {
          setOpenWindows(windows);

          const highestZ = windows.reduce((max, w) => Math.max(max, w.zIndex || 0), 100);
          const restoredZ = Math.max(remote.nextZIndex || 100, highestZ + 1);
          setNextZIndex(restoredZ);
          setDesktopStateRowId(row.id);
          console.log('[useOS] Applied remote restore.');
        } else {
          // Do not overwrite any windows that were opened (e.g., via operator URL) during boot
          setDesktopStateRowId(row.id);
          console.log('[useOS] Skipping remote restore: windows already open or no remote windows.');
        }
      } else {
        setDesktopStateRowId(row?.id ?? null);
      }
    };

    loadRemote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Save remote desktop state whenever it changes (when logged in)
  useEffect(() => {
    if (!userId) return;
    const payload = {
      windows: openWindows,
      nextZIndex,
      savedAt: new Date().toISOString(),
    };

    const saveRemote = async () => {
      // Use upsert to avoid 409 conflict on unique constraint (user_id, config_key)
      const { data } = await sb
        .from('os_user_desktop')
        .upsert(
          {
            id: desktopStateRowId ?? undefined,
            user_id: userId,
            config_key: 'windows_state_v1',
            config_value: payload,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id,config_key' }
        )
        .select('id')
        .single();

      if (data?.id) setDesktopStateRowId(data.id);
    };

    saveRemote();
  }, [openWindows, nextZIndex, userId]);

  // Desktop icons configuration
  const [desktopIcons, setDesktopIcons] = useState<DesktopIcon[]>(() => [
    {
      id: 'my-computer',
      appId: 'file-explorer',
      label: 'My Computer',
      icon: makeProgramIcon('my-computer', 'monitor'),
      x: 20,
      y: 20,
      filePath: 'My Computer'
    },
    {
      id: 'my-documents',
      appId: 'file-explorer',
      label: 'My Documents',
      icon: makeProgramIcon('my-documents', 'folder'),
      x: 20,
      y: 120,
      filePath: 'C:\\User\\User\\Documents'
    },
    {
      id: 'ai-center',
      appId: 'ai-center',
      label: 'AI Center',
      icon: PROGRAM_MAP['ai-center']?.icon,
      x: 20,
      y: 220
    },
    {
      id: 'control-panel',
      appId: 'control-panel',
      label: 'Control Panel',
      icon: PROGRAM_MAP['control-panel']?.icon,
      x: 20,
      y: 320
    },
    {
      id: 'recycle-bin',
      appId: 'file-explorer',
      label: 'Recycle Bin',
      icon: PROGRAM_MAP['recycle-bin']?.icon,
      x: 20,
      y: 420,
      filePath: 'C:\\$Recycle.Bin'
    },
    {
      id: 'music-player',
      appId: 'music-player',
      label: 'Music Player',
      icon: PROGRAM_MAP['music-player']?.icon,
      x: 20,
      y: 520
    },
    {
      id: 'terminal',
      appId: 'terminal',
      label: 'Terminal',
      icon: PROGRAM_MAP['terminal']?.icon,
      x: 20,
      y: 620
    },
    {
      id: 'office-suite',
      appId: 'office-suite',
      label: 'Office Suite',
      icon: PROGRAM_MAP['office-suite']?.icon,
      x: 120,
      y: 20
    },
    {
      id: 'disk-defrag',
      appId: 'disk-defrag',
      label: 'Disk Defragmenter',
      icon: PROGRAM_MAP['disk-defrag']?.icon,
      x: 20,
      y: 720
    },
    {
      id: 'paint-program',
      appId: 'paint-program',
      label: 'Paint',
      icon: PROGRAM_MAP['paint-program']?.icon,
      x: 120,
      y: 320
    },
    {
      id: 'web-browser',
      appId: 'web-browser',
      label: 'Navigator',
      icon: PROGRAM_MAP['web-browser']?.icon || makeProgramIcon('web-browser', 'document'),
      x: 120,
      y: 120
    },

    {
      id: 'dos-emulator',
      appId: 'DOSEmulator',
      label: 'DOS Emulator',
      icon: PROGRAM_MAP['DOSEmulator']?.icon || makeProgramIcon('dos-emulator', 'console'),
      x: 120,
      y: 220
    },
    {
      id: 'code-editor',
      appId: 'code-editor',
      label: 'Code Editor',
      icon: PROGRAM_MAP['code-editor']?.icon || makeProgramIcon('code-editor', 'source-code'),
      x: 220,
      y: 20
    },
    {
      id: 'maple-gis',
      appId: 'maple-gis',
      label: 'MapleGIS',
      icon: PROGRAM_MAP['maple-gis']?.icon,
      x: 220,
      y: 120
    },
    {
      id: 'screenshot-tool',
      appId: 'screenshot-tool',
      label: 'Screenshots',
      icon: PROGRAM_MAP['screenshot-tool']?.icon || makeProgramIcon('screenshot-tool', 'image'),
      x: 220,
      y: 220
    },
    {
      id: 'messenger',
      appId: 'messenger',
      label: 'Messenger',
      icon: PROGRAM_MAP['messenger']?.icon || makeProgramIcon('messanger', 'artificial-intelligence'),
      x: 220,
      y: 320
    }

  ]);

  // Listen for custom events to open apps
  useEffect(() => {
    const handleOpenFileInApp = (event: CustomEvent) => {
      const { appId, filePath, content } = event.detail;
      
      if (appId === 'hex-editor') {
        openWindow('hex-editor', 'Hex Editor', filePath, content);
      }
    };

    window.addEventListener('openFileInApp', handleOpenFileInApp as EventListener);
    
    return () => {
      window.removeEventListener('openFileInApp', handleOpenFileInApp as EventListener);
    };
  }, [openWindow]);

  // Listen for operator requests to open/focus windows
  useEffect(() => {
    const handler = (ev: Event) => {
      const custom = ev as CustomEvent<{ appId: string; title?: string; route?: string; filePath?: string; fileContent?: string }>;
      const { appId, title, route, filePath, fileContent } = custom.detail || {} as any;
      console.log('[useOS] operator:openWindow received', { appId, title, route, filePath, fileContent });
      if (!appId) return;

      // Special-case MapleAccess to avoid duplicates: focus existing or open once
      if (appId === 'maple-access') {
        const existingAccess = openWindows.find(w => w.appId === 'maple-access');
        if (existingAccess) {
          focusWindow(existingAccess.id);
          if (typeof filePath === 'string' && filePath.length > 0) {
            window.dispatchEvent(new CustomEvent('maple-access:open-target', { detail: { filePath } }));
          }
          return;
        }
        // No instance yet → open once; MapleAccess will announce readiness and can accept open-target events
        openWindow(appId, title ?? appId, filePath, fileContent, { route, focus: true });
        return;
      }

      // Default behavior for other apps
      openWindow(appId, title ?? appId, filePath, fileContent, { route, focus: true });
      console.log('[useOS] openWindow invoked', { appId, title: title ?? appId, filePath, route });
    };
    window.addEventListener('operator:openWindow', handler as EventListener);
    return () => window.removeEventListener('operator:openWindow', handler as EventListener);
  }, [openWindow, openWindows, focusWindow]);

  // Listen for template application requests from UI
  useEffect(() => {
    const handler = (ev: Event) => {
      const custom = ev as CustomEvent<{ name: string; vars?: Record<string, string> }>;
      const { name, vars } = custom.detail || { name: '', vars: {} };
      if (!name) return;
      applyTemplate(name, vars || {});
    };
    window.addEventListener('mapleos-apply-template', handler as EventListener);
    return () => window.removeEventListener('mapleos-apply-template', handler as EventListener);
  }, [applyTemplate]);

  // Listen for operator notify events and surface toasts
  useEffect(() => {
    const handler = (e: Event) => {
      const { title, message } = (e as CustomEvent<{ title?: string; message?: string }>).detail || {};
      toast({ title: title || 'OS Action', description: message || '' });
    };
    window.addEventListener('operator:notify', handler as EventListener);
    return () => window.removeEventListener('operator:notify', handler as EventListener);
  }, [toast]);

  useEffect(() => {
    const handler = (ev: Event) => {
      const { appId } = (ev as CustomEvent<{ appId?: string }>).detail || {};
      if (!appId) return;

      if (appId === 'ai-chat') {
        clearPersistedAiChatTabs();
      }

      clearPersistedWindowStateForApp(appId);
      setOpenWindows((prev) => removeAppWindowsFromSnapshot(prev, appId));
    };
    window.addEventListener('mapleos:recover-app-state', handler as EventListener);
    return () => window.removeEventListener('mapleos:recover-app-state', handler as EventListener);
  }, []);

  useEffect(() => {
    const handler = () => {
      setOpenWindows([]);
      setNextZIndex(100);
      setIsStartMenuOpen(false);
    };

    window.addEventListener('mapleos:reset-broken-windows', handler as EventListener);
    return () => window.removeEventListener('mapleos:reset-broken-windows', handler as EventListener);
  }, []);

  const forceCloseWindow = useCallback((windowId: string) => {
    setOpenWindows(prev => prev.filter((w) => w.id !== windowId));
  }, []);

  useEffect(() => {
    const handler = (ev: Event) => {
      const { windowId } = (ev as CustomEvent<{ windowId?: string }>).detail || {};
      if (!windowId) return;
      forceCloseWindow(windowId);
    };
    window.addEventListener('mapleos:force-close-window', handler as EventListener);
    return () => window.removeEventListener('mapleos:force-close-window', handler as EventListener);
  }, [forceCloseWindow]);

  const closeWindow = useCallback((windowId: string) => {
    setOpenWindows(prev => {
      const targetWindow = prev.find((w) => w.id === windowId);

      if (targetWindow?.appId === 'messenger') {
        return prev.map((w) =>
          w.id === windowId
            ? { ...w, isMinimized: true, hiddenInTray: true }
            : w
        );
      }

      return prev.filter((w) => w.id !== windowId);
    });
  }, []);

  const minimizeWindow = useCallback((windowId: string) => {
    setOpenWindows(prev => 
      prev.map(w => 
        w.id === windowId ? { ...w, isMinimized: !w.isMinimized, hiddenInTray: false } : w
      )
    );
  }, []);

  const updateWindowBounds = useCallback((windowId: string, bounds: { x: number; y: number; width: number; height: number }) => {
    setOpenWindows(prev =>
      prev.map(w =>
        w.id === windowId
          ? {
              ...w,
              x: bounds.x,
              y: bounds.y,
              width: bounds.width,
              height: bounds.height,
            }
          : w
      )
    );
  }, []);

  const maximizeWindow = useCallback((windowId: string) => {
    setOpenWindows(prev => 
      prev.map(w => {
        if (w.id === windowId) {
          if (w.isMaximized) {
            // Restore window
            return {
              ...w,
              isMaximized: false,
              x: w.originalX ?? w.x,
              y: w.originalY ?? w.y,
              width: w.originalWidth ?? w.width,
              height: w.originalHeight ?? w.height
            };
          } else {

            // Maximize window
            return {
              ...w,
              isMaximized: true,
              originalX: w.x,
              originalY: w.y,
              originalWidth: w.width,
              originalHeight: w.height,
              x: 0,
              y: 0,
              width: globalThis.window.innerWidth,
              height: globalThis.window.innerHeight - 48 // 48px for taskbar
            };
          }
        }
        return w;
      })
    );
  }, []);

  const toggleStartMenu = useCallback(() => {
    setIsStartMenuOpen(prev => !prev);
  }, []);

  const updateActivity = useCallback(() => {
    setLastActivity(Date.now());
  }, []);

  const updateDesktopIconPosition = useCallback((iconId: string, x: number, y: number) => {
    setDesktopIcons(prev => 
      prev.map(icon => 
        icon.id === iconId ? { ...icon, x, y } : icon
      )
    );
  }, []);

  const addDesktopIcon = useCallback((icon: DesktopIcon) => {
    setDesktopIcons(prev => [...prev, icon]);
  }, []);

  const removeDesktopIcon = useCallback((iconId: string) => {
    setDesktopIcons(prev => prev.filter(icon => icon.id !== iconId));
  }, []);

  const updateDesktopIcon = useCallback((iconId: string, updates: Partial<DesktopIcon>) => {
    setDesktopIcons(prev => 
      prev.map(icon => 
        icon.id === iconId ? { ...icon, ...updates } : icon
      )
    );
  }, []);

  return {
    openWindows,
    openWindow,
    closeWindow,
    forceCloseWindow,
    minimizeWindow,
    maximizeWindow,
    updateWindowBounds,
    focusWindow,
    isStartMenuOpen,
    toggleStartMenu,
    desktopIcons,
    addDesktopIcon,
    updateDesktopIconPosition,
    removeDesktopIcon,
    updateDesktopIcon,
    lastActivity,
    updateActivity,
    getDesktopRoot,
  };
};